import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { getPortalHelpTopic } from './topics.ts';

/**
 * The patient portal's own small, quiet, deep-linking help affordance — the same shape
 * `components/HelpLink.tsx` already establishes for the staff app
 * (`claude-kit/rules/invariants.md`'s in-app-help section), rebuilt rather than reused because that
 * component is staff-specific in two ways that do not transfer: it reads `@emr/shared`'s
 * `getHelpTopic` (the staff topic set), and it links to the fixed `/help/:topicId` path, which has no
 * `:orgId` segment. Every patient-portal route lives under `/portal/:orgId/*`
 * (`App.tsx`'s own routing comment), so this component reads `orgId` from the URL itself rather than
 * asking every call site to pass it — the same convenience `HelpLink`'s own zero-extra-prop shape
 * has, just resolved against this surface's own routing instead.
 *
 * A real `<Link>`, not a `<button>` — supports middle-click and right-click-copy the ordinary way a
 * link does. Visually understated: an outlined glyph in muted ink, not a chip competing for
 * attention. Its accessible name states the actual topic ("Help: signing in and your session")
 * rather than announcing the glyph itself to a screen reader — `HelpLink.tsx`'s own convention,
 * matched exactly.
 *
 * `topic.body` is never duplicated here — this renders nothing but a link to
 * `/portal/:orgId/help/:topicId`, so the one place the explanation's text actually lives stays
 * `topics.ts`'s `PORTAL_HELP_TOPICS` (`invariants.md`'s "nothing is written twice").
 *
 * Renders nothing for an id that does not match any topic, or when mounted outside a
 * `/portal/:orgId/*` route — the same stale-link tolerance `HelpLink.tsx` applies, extended to a
 * missing `orgId` since a help link with nowhere valid to point is worse than no link at all.
 */
export function PortalHelpLink({ topicId }: { readonly topicId: string }) {
  const { t } = useTranslation('portalHelp');
  const { orgId } = useParams<{ orgId?: string }>();
  const topic = getPortalHelpTopic(topicId);
  if (topic === null || orgId === undefined) return null;

  // `defaultValue` falls back to the English source (`topics.ts`) for a topic `portalHelp.json` has
  // no translation for yet — never a raw, untranslated i18next key string.
  const title = t(`topics.${topic.id}.title`, { defaultValue: topic.title });
  const summary = t(`topics.${topic.id}.summary`, { defaultValue: topic.summary });

  return (
    <Link
      to={`/portal/${orgId}/help/${topic.id}`}
      title={summary}
      aria-label={t('helpLink.ariaLabel', { title })}
      className="inline-flex size-5 shrink-0 items-center justify-center rounded-pill border border-line text-xs font-semibold text-ink-muted hover:border-line-strong hover:text-ink"
    >
      <span aria-hidden="true">?</span>
    </Link>
  );
}
